import * as React from "react";
import { useHistory } from "react-router-dom";
import Slider, { Settings } from "react-slick";
import clsx from "clsx";
import { useSwipeable } from "react-swipeable";

import { ReactComponent as IconArrow } from "../../../assets/svg/caret_down.svg";
import useDebounceCallback from "../../../hooks/useDebounceCallback";
import { parseQueryParams } from "../../../libs/Util";
import { ILesson } from "../../../state";
import { ReactComponent as IconClose } from "../../../assets/svg/close.svg";
import ModalAnswers from "../ModalAnswers";
import StepProcessBar from "../../../organisms/teacher/StepProcessBar";
import ModalThreeRules from "../../../organisms/teacher/ModalThreeRules";
import Timer from "../../../organisms/teacher/Timer";
import VideoJS from "../../../atoms/VideoJS";
import {
  ARROW_DOWN,
  ARROW_RIGHT,
  ARROW_UP,
  ARROW_LEFT,
} from "../../../constants/key";
import ImagePageView from "../../../organisms/teacher/ImagePageView";
import ModalEditGroupClass from "../ModalEditGroupClass";

import styles from "./MaterialForImage.module.scss";

export interface MaterialForImageProps {
  teachingMaterialId: number;
  lesson: ILesson;
  classList: number[];
  prevLessonId: number | null;
  nextLessonId: number | null;
}

interface SubPageState {
  page: number;
  subPage: number;
}

const settings: Settings = {
  accessibility: false,
  arrows: false,
  dots: false,
  infinite: false,
  speed: 300,
  slidesToShow: 1,
  slidesToScroll: 1,
  swipe: false,
  vertical: true,
  waitForAnimate: false,
};

const existsPage = (images: string[], page: number) => !!images[page - 1];

const existsSubPage = (subImages: string[][], page: number, subPage: number) =>
  subImages[page - 1] && subImages[page - 1][subPage - 1];

const MaterialForImage = (props: MaterialForImageProps) => {
  const history = useHistory();
  const slider = React.useRef<Slider>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [wrapperSize, setWrapperSize] = React.useState({ width: 0, height: 0 });
  const stepRef = React.useRef<HTMLDivElement>(null);
  const [debounceClickOutside] = useDebounceCallback(
    (listButton: HTMLCollectionOf<Element>) => {
      setTimeout(() => {
        if (stepRef.current) {
          stepRef.current.style.opacity = "";
          stepRef.current.style.transition = "";
        }
      }, 500);
      for (let i = 0; i < listButton.length; i++) {
        setTimeout(() => {
          if (listButton.item(i)?.classList.contains(styles.buttonFadeIn)) {
            listButton.item(i)?.classList.remove(styles.buttonFadeIn);
          }
        }, 1700);
      }
    },
  );
  const [debounceClick] = useDebounceCallback((action: () => void) => {
    action();
  });
  const [debounceSwipe] = useDebounceCallback((action: () => void) => {
    action();
  }, 400);

  const [isShowAnswersModal, setIsShowAnswersModal] = React.useState(false);
  const [isShowThreeRules, setIsShowThreeRules] = React.useState(false);
  const [showVideo, setShowVideo] = React.useState(false);
  const [showModalEditGroupClass, setShowModalEditGroupClass] = React.useState(
    false,
  );
  const [showTimer, setShowTimer] = React.useState(false);
  const [videoOptions, setVideoOptions] = React.useState({
    url: "",
    playing: false,
  });
  const prmPage = Number(parseQueryParams()["page"]) || 1;
  const prmSub = Number(parseQueryParams()["sub_page"]) || 0;
  const [
    prevSubPageState,
    setPrevSubPageState,
  ] = React.useState<SubPageState | null>(null);
  const isShowSub = !!props.lesson.sub_images && prmSub > 0;
  const subImages = React.useMemo(() => props.lesson.sub_images || [], [
    props.lesson.sub_images,
  ]);
  const currentPage = existsPage(props.lesson.images, prmPage) ? prmPage : 1;
  const currentSubPage =
    isShowSub && existsSubPage(subImages, prmPage, prmSub) ? prmSub : 1;
  const currentFlatSubPage = React.useMemo(
    () =>
      isShowSub
        ? subImages.slice(0, currentPage - 1).flat().length + currentSubPage
        : 1,
    [isShowSub, subImages, currentPage, currentSubPage],
  );
  const currentVideo = React.useMemo(
    () =>
      props.lesson.videos?.find(
        video =>
          video.page === currentPage &&
          (!isShowSub || video.sub_page === currentSubPage),
      ),
    [isShowSub, currentPage, currentSubPage, props.lesson.videos],
  );

  const flattenSubImages = React.useMemo(() => subImages.flat(), [subImages]);

  const currentIssue = React.useMemo(
    () => props.lesson.issues?.find(issue => issue.page === currentPage),
    [props.lesson.issues, currentPage],
  );
  const issueID = currentIssue?.id || 0;

  React.useEffect(() => {
    const nextIdx = isShowSub ? currentFlatSubPage - 1 : currentPage - 1;
    slider.current?.slickGoTo(nextIdx, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentSubPage]);

  React.useEffect(() => {
    if (props.lesson) {
      if (currentVideo) {
        setVideoOptions({
          url: currentVideo.video_url,
          playing: false,
        });
      } else {
        setVideoOptions({
          url: "",
          playing: false,
        });
      }
    }
  }, [props.lesson, currentVideo]);

  React.useEffect(() => {
    const changePageByKeyBoard = (e: any) => {
      if (/input|textarea/i.test(e.target.tagName)) return;

      if (e.key === ARROW_DOWN || e.key === ARROW_RIGHT) {
        debounceClick(nextFunc);
      }

      if (e.key === ARROW_UP || e.key === ARROW_LEFT) {
        debounceClick(prevFunc);
      }
    };

    if (isShowAnswersModal || isShowThreeRules) {
      document.removeEventListener("keydown", changePageByKeyBoard);
    } else {
      document.addEventListener("keydown", changePageByKeyBoard);
    }

    return () => {
      document.removeEventListener("keydown", changePageByKeyBoard);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isShowSub,
    currentPage,
    currentSubPage,
    isShowAnswersModal,
    isShowThreeRules,
  ]);

  const disablePrev = currentPage === 1 && (!isShowSub || currentSubPage === 1);

  const disableNext = isShowSub
    ? currentFlatSubPage >= flattenSubImages.length
    : currentPage >= props.lesson.images.length;

  const setNewPage = (newPage: number, newSubPage?: number) => {
    const query = newSubPage
      ? `?teachingMaterial=${props.teachingMaterialId}&lesson=${props.lesson.id}&page=${newPage}&sub_page=${newSubPage}&classList=${props.classList}`
      : `?teachingMaterial=${props.teachingMaterialId}&lesson=${props.lesson.id}&page=${newPage}&classList=${props.classList}`;
    history.push({
      search: query,
    });
  };

  const prevFunc = () => {
    const isZoom = Math.abs(document.body.offsetWidth - window.innerWidth) > 3;
    if (isZoom || disablePrev) {
      return;
    }
    if (isShowSub) {
      const prevPage = currentSubPage > 1 ? currentPage : currentPage - 1;
      const prevSubPage =
        currentSubPage > 1
          ? currentSubPage - 1
          : subImages[prevPage - 1].length;
      setNewPage(prevPage, prevSubPage);
    } else {
      setNewPage(currentPage - 1);
    }
  };

  const nextFunc = () => {
    const isZoom = Math.abs(document.body.offsetWidth - window.innerWidth) > 3;
    if (isZoom || disableNext) {
      return;
    }
    if (isShowSub) {
      const exists = currentSubPage < subImages[currentPage - 1].length;
      const nextPage = exists ? currentPage : currentPage + 1;
      const nextSubPage = exists ? currentSubPage + 1 : 1;
      setNewPage(nextPage, nextSubPage);
    } else {
      setNewPage(currentPage + 1);
    }
  };

  const onChangeLesson = (lessonId: number) => {
    history.push({
      search: `?teachingMaterial=${props.teachingMaterialId}&lesson=${lessonId}&page=1&classList=${props.classList}`,
    });
  };

  const prevLesson = () => {
    props.prevLessonId && onChangeLesson(props.prevLessonId);
  };

  const nextLesson = () => {
    props.nextLessonId && onChangeLesson(props.nextLessonId);
  };

  const handlers = useSwipeable({
    onSwipedUp: () => debounceSwipe(nextFunc),
    onSwipedDown: () => debounceSwipe(prevFunc),
    delta: 60,
    swipeDuration: 500,
  });

  const refPassthrough = (el: HTMLDivElement) => {
    // call useSwipeable ref prop with el
    handlers.ref(el);

    // set myRef el so you can access it yourself
    //@ts-ignore
    wrapperRef.current = el;
  };

  const handleWrapperRef = () => {
    if (wrapperRef.current) {
      setWrapperSize({
        width: wrapperRef.current.offsetWidth,
        height: wrapperRef.current.offsetHeight,
      });
    }
  };

  React.useEffect(() => {
    handleWrapperRef();
    window.addEventListener("resize", handleWrapperRef);

    return () => {
      window.removeEventListener("resize", handleWrapperRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef.current]);

  const getListAnswerPages = () => {
    return props.lesson.issues!.map(issue => issue.page);
  };

  const handleClickOutside = () => {
    const listButton = document.getElementsByClassName(styles.paginate);
    if (stepRef.current) {
      stepRef.current.style.opacity = "1";
      stepRef.current.style.transition = "0s";
    }
    for (let i = 0; i < listButton.length; i++) {
      if (!listButton.item(i)?.classList.contains(styles.buttonFadeIn)) {
        listButton.item(i)?.classList.add(styles.buttonFadeIn);
      }
    }
    debounceClickOutside(listButton);
  };

  const handleCloseVideo = () => {
    setVideoOptions(prevState => ({
      ...prevState,
      playing: false,
    }));
    setShowVideo(false);
  };

  const handleOpenVideo = () => {
    setShowVideo(true);
    if (currentVideo) {
      setVideoOptions({
        url: currentVideo.video_url,
        playing: false,
      });
    } else {
      setVideoOptions({
        url: "",
        playing: false,
      });
    }
  };

  return (
    <div className="d-flex h-100">
      <div className={styles.wrapper} {...handlers} ref={refPassthrough}>
        <button
          className={styles.paginate}
          disabled={disablePrev}
          onClick={() => {
            debounceClick(prevFunc);
          }}
        >
          <IconArrow />
        </button>
        <button
          className={styles.paginate}
          disabled={disableNext}
          onClick={() => {
            debounceClick(nextFunc);
          }}
        >
          <IconArrow />
        </button>
        <div onClick={handleClickOutside}>
          <Slider
            className={clsx(styles.slider)}
            onSwipe={swipeDirection => {
              if (swipeDirection === "up") {
                nextFunc();
              } else if (swipeDirection === "down") {
                prevFunc();
              }
            }}
            key={isShowSub ? "s" : "m"}
            ref={slider}
            initialSlide={isShowSub ? currentFlatSubPage - 1 : currentPage - 1}
            {...settings}
          >
            {(isShowSub ? flattenSubImages : props.lesson.images).map(
              (image, i) => {
                return (
                  <ImagePageView
                    imageUrl={image}
                    imageType={props.lesson.image_type}
                    page={i + 1}
                    outerSize={wrapperSize}
                    key={i}
                  />
                );
              },
            )}
          </Slider>
        </div>
        {!isShowSub && (
          <StepProcessBar
            totalPage={props.lesson.images.length}
            currentPage={currentPage}
            listAnswerPages={getListAnswerPages()}
            ref={stepRef}
          />
        )}
      </div>
      <div
        className={styles.video}
        style={{
          display: showVideo && videoOptions.url.length > 0 ? "block" : "none",
        }}
      >
        <div className={styles.close} onClick={handleCloseVideo}>
          <IconClose />
        </div>
        {!!videoOptions.url && (
          <VideoJS
            key={videoOptions.url}
            className={styles.videojs}
            playing={videoOptions.playing}
            showVideo={showVideo}
            sourceUrl={videoOptions.url}
          />
        )}
      </div>
      {showTimer && (
        <div className={styles.timer}>
          <Timer
            className={styles.timer__timerInShareScreen}
            onClose={() => {
              setShowTimer(false);
            }}
          />
        </div>
      )}
      {isShowThreeRules && (
        <ModalThreeRules
          onClose={() => {
            setIsShowThreeRules(false);
          }}
        />
      )}
      {isShowAnswersModal && (
        <ModalAnswers
          is_display_all_schools_answer={
            !!props.lesson.is_display_all_schools_answer
          }
          issueID={issueID}
          classIDs={props.classList}
          isMaterial
          onClose={() => {
            setIsShowAnswersModal(false);
          }}
        />
      )}
      {showModalEditGroupClass && (
        <ModalEditGroupClass
          classIDs={props.classList}
          onClose={() => setShowModalEditGroupClass(false)}
        />
      )}
      <div className={styles.rightSideBar}>
        <div className={styles.topButtons}>
          <button
            disabled={!currentVideo}
            className={clsx(styles.actionBtn, styles.primary)}
            onClick={handleOpenVideo}
          >
            動画
          </button>
          <button
            disabled={!currentIssue}
            className={clsx(styles.actionBtn, styles.primary)}
            onClick={() => {
              setIsShowAnswersModal(true);
            }}
          >
            みんなの回答
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => {
              setIsShowThreeRules(!isShowThreeRules);
            }}
          >
            3つのルール
          </button>

          <button
            className={clsx(styles.actionBtn)}
            onClick={() => {
              setShowTimer(!showTimer);
            }}
          >
            タイマー
          </button>
        </div>
        <div className={styles.settingButtons}>
          <button
            className={styles.actionBtn}
            onClick={() => {
              setShowModalEditGroupClass(!showModalEditGroupClass);
            }}
          >
            グループ設定
          </button>
          {props.lesson.sub_images && (
            <button
              className={styles.toggleSubImages}
              onClick={() => {
                if (isShowSub) {
                  setPrevSubPageState({
                    page: currentPage,
                    subPage: currentSubPage,
                  });
                  setNewPage(currentPage);
                } else {
                  let newSubPage = 1;
                  if (
                    prevSubPageState &&
                    prevSubPageState.page === currentPage
                  ) {
                    newSubPage = prevSubPageState.subPage;
                  }
                  setNewPage(currentPage, newSubPage);
                }
              }}
            >
              <div
                className={clsx(
                  styles.toggleBtn,
                  isShowSub && styles.activeBtn,
                )}
              >
                補助スライド
              </div>
              <div
                className={clsx(
                  styles.toggleBtn,
                  !isShowSub && styles.activeBtn,
                )}
              >
                教材
              </div>
            </button>
          )}
        </div>
        <div className={styles.bottomButtons}>
          <button
            className={styles.actionBtn}
            disabled={!props.prevLessonId}
            onClick={prevLesson}
          >
            前のLesson
          </button>
          <button
            className={styles.actionBtn}
            disabled={!props.nextLessonId}
            onClick={nextLesson}
          >
            次のLesson
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MaterialForImage);
